import React, { useState, useEffect } from 'react';
import { usersAPI, locationsAPI, authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserCog, Shield, Stethoscope, ClipboardList, Package, KeyRound, Copy, Crown, UserPlus, LogIn, Search, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataPagination from '../components/DataPagination';

const roleIcons = {
  super_admin: Crown,
  clinic_admin: Shield,
  doctor: Stethoscope,
  front_desk: ClipboardList,
  inventory_manager: Package
};

const Users = () => {
  const { user: currentUser, isSuperAdmin, isClinicAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newUserPasswordDialogOpen, setNewUserPasswordDialogOpen] = useState(false);
  const [provisionalPassword, setProvisionalPassword] = useState('');
  const [newUserData, setNewUserData] = useState(null);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'front_desk',
    location_ids: [],
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, locationsRes] = await Promise.all([
        usersAPI.getAll({ page: currentPage, page_size: pageSize, search: searchQuery || undefined }),
        locationsAPI.getAll()
      ]);
      
      // Handle paginated users response
      const paginatedUsers = usersRes.data;
      setUsers(paginatedUsers.items || []);
      setTotalItems(paginatedUsers.total || 0);
      setTotalPages(paginatedUsers.total_pages || 0);
      
      // Handle locations (might be paginated)
      const locData = locationsRes.data.items || locationsRes.data;
      setLocations(Array.isArray(locData) ? locData : []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          location_ids: formData.location_ids.length > 0 ? formData.location_ids : null,
          is_active: formData.is_active
        };
        await usersAPI.update(editingUser.id, updateData);
        toast.success('User updated successfully');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        // Create user without password - backend will auto-generate
        const response = await usersAPI.create({
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          location_ids: formData.location_ids.length > 0 ? formData.location_ids : null
        });
        
        // Show the generated password dialog
        setNewUserData(response.data);
        setProvisionalPassword(response.data.provisional_password);
        setDialogOpen(false);
        setNewUserPasswordDialogOpen(true);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await usersAPI.resetPassword(userId);
      setProvisionalPassword(response.data.provisional_password);
      setSelectedUserForReset(users.find(u => u.id === userId));
      setResetPasswordDialogOpen(true);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Are you sure you want to impersonate ${targetUser.full_name}? You will be logged in as this user.`)) {
      return;
    }
    try {
      const response = await authAPI.impersonate(targetUser.id);
      const { access_token, user, clinic } = response.data;
      
      // Store the new token and user data
      localStorage.setItem('clinic_token', access_token);
      localStorage.setItem('clinic_user', JSON.stringify(user));
      if (clinic) {
        localStorage.setItem('clinic_info', JSON.stringify(clinic));
      }
      
      toast.success(`Now logged in as ${targetUser.full_name}`);
      
      // Redirect based on role
      if (user.role === 'doctor') {
        navigate('/visits');
      } else {
        navigate('/dashboard');
      }
      
      // Force page reload to refresh auth context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'front_desk',
      location_ids: currentUser?.location_ids || [],
      is_active: true
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      location_ids: user.location_ids || [],
      is_active: user.is_active
    });
    setDialogOpen(true);
  };

  const toggleLocation = (locationId) => {
    setFormData(prev => {
      const currentIds = prev.location_ids || [];
      if (currentIds.includes(locationId)) {
        return { ...prev, location_ids: currentIds.filter(id => id !== locationId) };
      } else {
        return { ...prev, location_ids: [...currentIds, locationId] };
      }
    });
  };

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    if (isSuperAdmin()) {
      return ['super_admin', 'admin', 'doctor', 'front_desk', 'inventory_manager'];
    } else if (isClinicAdmin()) {
      return ['doctor', 'front_desk', 'inventory_manager'];
    }
    return [];
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-amber-50 text-amber-700 border-amber-200',
      admin: 'bg-purple-50 text-purple-700 border-purple-200',
      doctor: 'bg-blue-50 text-blue-700 border-blue-200',
      front_desk: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      inventory_manager: 'bg-orange-50 text-orange-700 border-orange-200'
    };
    const Icon = roleIcons[role] || UserCog;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[role] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
        <Icon className="w-3 h-3" />
        {role?.replace('_', ' ')}
      </span>
    );
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const location = locations.find(l => l.id === locationId);
    return location?.name || 'Unknown';
  };

  const canEditUser = (user) => {
    // Super admin can edit anyone except themselves (to prevent lockout)
    if (isSuperAdmin() && user.id !== currentUser?.id) return true;
    // Admin can edit non-super-admin, non-admin users in their location
    if (isClinicAdmin() && !['super_admin', 'admin'].includes(user.role)) return true;
    return false;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isSuperAdmin() ? 'Manage all staff accounts and permissions' : 'Manage staff accounts for your clinic'}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update user account details' : 'Create a new staff account'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <DialogBody className="space-y-4">
                <div>
                  <Label htmlFor="full_name" className="text-slate-700">Full Name *</Label>
                  <Input
                    id="full_name"
                    placeholder="e.g., Dr. John Smith"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="mt-1.5"
                    data-testid="user-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@clinic.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1.5"
                    data-testid="user-email-input"
                  />
                </div>
                {!editingUser && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      <strong>Note:</strong> A temporary password will be auto-generated and shown after creation. The user must change it on first login.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="role" className="text-slate-700">Role *</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(val) => setFormData({ ...formData, role: val })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableRoles().map(role => {
                          const Icon = roleIcons[role] || UserCog;
                          return (
                            <SelectItem key={role} value={role}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" /> {role.replace('_', ' ')}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-orange-500" />
                      Assigned Locations
                    </Label>
                    <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto bg-slate-50" data-testid="user-locations-select">
                      {locations.length === 0 ? (
                        <p className="text-sm text-slate-500">No locations available</p>
                      ) : (
                        <>
                          {locations.map((loc) => (
                            <label 
                              key={loc.id} 
                              className="flex items-center gap-2 cursor-pointer hover:bg-white p-1.5 rounded transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={formData.location_ids?.includes(loc.id) || false}
                                onChange={() => toggleLocation(loc.id)}
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">{loc.name}</span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">
                      {formData.location_ids?.length > 0 
                        ? `User can see data from ${formData.location_ids.length} selected location(s)` 
                        : "No locations selected - user can see all locations"}
                    </p>
                  </div>
                </div>
                {editingUser && (
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active" className="text-slate-700">Active account</Label>
                  </div>
                )}
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="save-user-btn">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Password Reset
            </DialogTitle>
            <DialogDescription>
              A temporary password has been generated for {selectedUserForReset?.full_name}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-600 uppercase tracking-wider mb-2 font-medium">Temporary Password</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border border-amber-200">
                  {provisionalPassword}
                </code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(provisionalPassword)}
                  className="border-amber-300"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Share this password securely with the user. They will be required to set a new password on their next login.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setResetPasswordDialogOpen(false)} className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New User Password Dialog */}
      <Dialog open={newUserPasswordDialogOpen} onOpenChange={setNewUserPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-user-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              User Created Successfully
            </DialogTitle>
            <DialogDescription>
              {newUserData?.full_name} has been created with role: {newUserData?.role?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-600 uppercase tracking-wider mb-2 font-medium">Auto-Generated Password</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border border-green-200" data-testid="generated-password">
                  {provisionalPassword}
                </code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(provisionalPassword)}
                  className="border-green-300"
                  data-testid="copy-password-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>Email:</strong> {newUserData?.email}</p>
              <p className="text-amber-600">
                Please share these credentials securely with the user. They will be required to set a new password on their first login.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => {
              setNewUserPasswordDialogOpen(false);
              setNewUserData(null);
              setProvisionalPassword('');
            }} className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="new-user-done-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state py-16">
              <UserCog className="empty-state-icon" />
              <p className="empty-state-title">No users found</p>
              <p className="empty-state-text">Add your first user to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-slate-600 font-medium">Name</TableHead>
                    <TableHead className="text-slate-600 font-medium">Email</TableHead>
                    <TableHead className="text-slate-600 font-medium">Role</TableHead>
                    <TableHead className="text-slate-600 font-medium">Assigned Locations</TableHead>
                    <TableHead className="text-slate-600 font-medium">Status</TableHead>
                    <TableHead className="text-right text-slate-600 font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-slate-600">
                              {user.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-slate-900">{user.full_name}</span>
                            {user.must_reset_password && (
                              <p className="text-xs text-amber-600">Must reset password</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>
                        {user.location_ids && user.location_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(user.location_names || user.location_ids.map(id => getLocationName(id))).map((name, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">
                                <MapPin className="w-3 h-3" />
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">All Locations</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={user.is_active ? 'badge-success' : 'badge-error'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {/* Impersonate button - Super Admin only, cannot impersonate self or other super admins */}
                        {isSuperAdmin() && user.role !== 'super_admin' && user.id !== currentUser?.id && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleImpersonate(user)}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            title="Login as this user"
                            data-testid={`impersonate-user-${user.id}`}
                          >
                            <LogIn className="w-4 h-4" />
                          </Button>
                        )}
                        {canEditUser(user) && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleResetPassword(user.id)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Reset Password"
                              data-testid={`reset-password-${user.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditDialog(user)}
                              className="text-slate-600 hover:text-slate-900"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        
        {/* Pagination */}
        <DataPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Card>
    </div>
  );
};

export default Users;
